import { Fragment, useEffect, useState } from 'react'

import { routes } from 'Routes'
import { useHistory } from 'react-router-dom'
import { reverse } from 'named-urls'
import { Controller, useForm } from 'react-hook-form'
import { Alert } from '@material-ui/lab'
import {
  Box,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography,
  FormHelperText,
} from '@material-ui/core'

import { usePermissions, useFetch, useSnackbar } from 'hooks'

import {
  ButtonComponent,
  Card,
  ConfirmationDialog,
  Label,
  LoadingFeedback,
} from 'components'
import ListContracts from '../ListContracts'
import { AvailablePreRegistrationsTable } from './components'

import helpers from 'helpers'
import * as service from 'service'
import constants from 'constants/index'
import schema from './schema'

const ChurnCreateForm = () => {
  const [loading, setLoading] = useState(false)
  const [selectedIds, setSelectedIds] = useState([])
  const [pendingData, setPendingData] = useState(null)
  const [openFidelityDialog, setOpenFidelityDialog] = useState(false)

  const history = useHistory()
  const snackbar = useSnackbar()
  const { permitted } = usePermissions()
  const handleEdit = (churnId) => {
    if (permitted(constants.permissions.CHURNS.UPDATE))
      return history.push(reverse(routes.churns.edit, { churnId }))

    snackbar.open({
      message:
        'A sua permissão esta limitada ao recurso de criação e por esse motivo voce sera redirecionado para a listagem. entre em contato com o suporte para pedir acesso a edição de churn',
      variant: 'info',
    })

    history.push(routes.churns.all)
  }

  const { control, handleSubmit, errors, watch, setValue } = useForm({
    validationSchema: schema,
    defaultValues: {
      paymentContract: { id: null, name: '' },
      origin: '',
      scope: constants.churn.TOTAL_SCOPE,
    },
  })

  const paymentContract = watch('paymentContract')
  const scope = watch('scope')
  const isPartial = scope === constants.churn.PARTIAL_SCOPE

  const { response: availableResponse, isLoading: isLoadingAvailable } =
    useFetch(
      service.dponet.churns.availablePreRegistrations,
      { paymentContractId: paymentContract?.id },
      [paymentContract?.id],
      !!paymentContract?.id,
    )

  const availablePreRegistrations =
    availableResponse?.data?.paymentContractPreRegistrations || []

  const partialDisabled =
    !!paymentContract?.id &&
    !isLoadingAvailable &&
    availablePreRegistrations.length <= 1

  useEffect(() => {
    if (partialDisabled) setValue('scope', constants.churn.TOTAL_SCOPE)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partialDisabled])

  useEffect(() => {
    setSelectedIds([])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentContract?.id])

  const onSubmit = async (data) => {
    if (isPartial && selectedIds.length === 0) {
      snackbar.open({
        message: 'Selecione ao menos uma organização para um churn parcial',
        variant: 'error',
      })
      return
    }

    if (paymentContract?.fidelityAlertMessage) {
      setPendingData(data)
      setOpenFidelityDialog(true)
      return
    }

    await createChurn(data)
  }

  const handleConfirmFidelity = async () => {
    setOpenFidelityDialog(false)
    await createChurn(pendingData)
  }

  const createChurn = async (data) => {
    setLoading(true)

    const params = {
      origin: data.origin,
      scope: data.scope,
      paymentContractId: data?.paymentContract?.id,
      ...(isPartial && { preRegistrationIds: selectedIds }),
    }

    try {
      const response = await service.dponet.churns.create(params)

      snackbar.open({
        message: 'Churn criado com sucesso!',
        variant: 'success',
      })

      handleEdit(response?.data?.churnId)
    } catch (error) {
      snackbar.open({
        message:
          helpers.formatters.errorMessage(error?.response?.data?.error) ||
          'Ocorreu um erro ao tentar criar o churn',
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <LoadingFeedback open={loading} />
      <Card title="Nova solicitação de churn">
        <Box mb={2}>
          <Alert severity="info">
            <Typography variant="body2">
              Para pesquisar por CNPJ/CPF, digite o número sem pontos, traços ou
              barras.
            </Typography>
          </Alert>
        </Box>
        <form onSubmit={handleSubmit(onSubmit)} id="form-churn-create">
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <ListContracts control={control} errors={errors} />
            </Grid>
            {paymentContract?.id && (
              <Fragment>
                <Grid item xs={12} md={6}>
                  <Label title="Qual é a origem do churn?">
                    <Controller
                      as={
                        <RadioGroup>
                          <FormHelperText error={!!errors?.origin}>
                            {errors?.origin?.message}
                          </FormHelperText>
                          <Box>
                            <FormControlLabel
                              value="solicitation"
                              control={<Radio color="primary" />}
                              label="Solicitação"
                            />
                          </Box>
                          <Box>
                            <FormControlLabel
                              value="non_payment"
                              control={<Radio color="primary" />}
                              label="Inadimplência"
                            />
                          </Box>
                          <Box>
                            <FormControlLabel
                              value="renewal"
                              control={<Radio color="primary" />}
                              label="Renovação"
                            />
                          </Box>
                        </RadioGroup>
                      }
                      control={control}
                      name="origin"
                      mode="onBlur"
                    />
                  </Label>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Label title="Escopo do churn">
                    <Controller
                      as={
                        <RadioGroup row>
                          <FormHelperText error={!!errors?.scope}>
                            {errors?.scope?.message}
                          </FormHelperText>
                          {constants.churn.SCOPES.map((item) => (
                            <FormControlLabel
                              key={item.id}
                              value={item.id}
                              control={<Radio color="primary" />}
                              label={item.name}
                              disabled={
                                item.id === constants.churn.PARTIAL_SCOPE &&
                                partialDisabled
                              }
                            />
                          ))}
                        </RadioGroup>
                      }
                      control={control}
                      name="scope"
                      mode="onBlur"
                    />
                  </Label>
                </Grid>
              </Fragment>
            )}
            {isPartial && paymentContract?.id && (
              <Grid item xs={12}>
                <Box mb={1}>
                  <Typography variant="subtitle2">
                    Selecione as organizações que farão parte deste churn
                  </Typography>
                </Box>
                <AvailablePreRegistrationsTable
                  availablePreRegistrations={availablePreRegistrations}
                  isLoading={isLoadingAvailable}
                  selectedIds={selectedIds}
                  setSelectedIds={setSelectedIds}
                />
              </Grid>
            )}
          </Grid>
          <Box display="flex" justifyContent="flex-end" mt={3}>
            <ButtonComponent
              type="submit"
              variant="contained"
              color="primary"
              title="Confirmar"
            />
          </Box>
        </form>
      </Card>
      {openFidelityDialog && (
        <ConfirmationDialog
          open={openFidelityDialog}
          setOpen={setOpenFidelityDialog}
          message="Atenção: contrato com fidelidade ativa"
          alert={paymentContract?.fidelityAlertMessage}
          alertColor="warning"
          buttonText="Estou ciente, prosseguir"
          cancelButtonText="Cancelar"
          actionAcceptButton={handleConfirmFidelity}
        />
      )}
    </>
  )
}

export default ChurnCreateForm
